import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, Send, Check, Pencil as PencilIcon } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { user } = useAuth();
  const canEdit = ["ADMIN", "MANAGER", "COORDINATOR"].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [groups, setGroups] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [raw, setRaw] = useState("");
  const [messages, setMessages] = useState([]);
  const [classifying, setClassifying] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [reviewForm, setReviewForm] = useState({ category: "", stage_id: "", substage_id: "", summary_line: "" });
  const [projectDetail, setProjectDetail] = useState(null);

  useEffect(() => {
    if (projectId) api.get(`/projects/${projectId}`).then((r) => setProjectDetail(r.data));
    else setProjectDetail(null);
  }, [projectId]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/groups").then((r) => setGroups(r.data));
  }, []);

  const loadMessages = (pid) => {
    if (!pid) { setMessages([]); return; }
    api.get(`/messages?project_id=${pid}`).then((r) => setMessages(r.data));
  };

  useEffect(() => { loadMessages(projectId); }, [projectId]);

  const projectGroups = groups.filter((g) => g.project_id === projectId);

  const parseAndIngest = async () => {
    // Parse lines like "Name: message" or plain lines
    const lines = raw.split("\n").map((l) => l.trim()).filter(Boolean);
    const parsed = lines.map((l) => {
      const m = l.match(/^([^:]{1,40}):\s*(.+)$/);
      return m ? { sender_name: m[1].trim(), body: m[2].trim() } : { sender_name: "Site", body: l };
    });
    try {
      const { data } = await api.post("/messages/ingest", { whatsapp_group_id: groupId, messages: parsed });
      toast.success(`${data.inserted} messages ingested`);
      setRaw("");
      loadMessages(projectId);
    } catch (e) { toast.error(apiError(e)); }
  };

  const classify = async () => {
    setClassifying(true);
    try {
      const { data } = await api.post(`/messages/classify?project_id=${projectId}`);
      toast.success(`${data.classified} messages classified by AI`);
      loadMessages(projectId);
    } catch (e) { toast.error(apiError(e)); }
    finally { setClassifying(false); }
  };

  const CATEGORIES = ["PROGRESS_UPDATE", "MATERIAL_MOVEMENT", "APPROVAL", "ISSUE_BLOCKER",
    "LABOUR", "INSPECTION", "PAYMENT_COMMERCIAL", "SCHEDULE", "GENERAL_CHATTER", "OTHER"];

  const markCorrect = async (messageId) => {
    try {
      await api.put(`/messages/${messageId}/classification/review`, { is_correct: true });
      toast.success("Marked correct");
      loadMessages(projectId);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openReview = (m) => {
    setReviewingId(m.id);
    setReviewForm({
      category: m.classification.category,
      stage_id: m.classification.project_stage_id || "",
      substage_id: m.classification.project_substage_id || "",
      summary_line: m.classification.summary_line,
    });
  };

  const submitCorrection = async (messageId) => {
    try {
      await api.put(`/messages/${messageId}/classification/review`, {
        is_correct: false,
        category: reviewForm.category,
        stage_id: reviewForm.stage_id || undefined,
        substage_id: reviewForm.substage_id || undefined,
        summary_line: reviewForm.summary_line,
      });
      toast.success("Correction saved");
      setReviewingId(null);
      loadMessages(projectId);
    } catch (e) { toast.error(apiError(e)); }
  };

  const substagesForStage = (stageId) =>
    (projectDetail?.stages || []).find((s) => s.id === stageId)?.substages || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Messages</h1>
        <p className="text-muted-foreground mt-1">Import group messages and classify them with AI.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Project</Label>
          <Select value={projectId} onValueChange={(v) => { setProjectId(v); setGroupId(""); }}>
            <SelectTrigger data-testid="msg-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Group</Label>
          <Select value={groupId} onValueChange={setGroupId} disabled={!projectId}>
            <SelectTrigger data-testid="msg-group-select"><SelectValue placeholder="Select group" /></SelectTrigger>
            <SelectContent>
              {projectGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.group_subject || g.group_jid}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {canEdit && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <Label>Paste WhatsApp messages (one per line, "Name: message")</Label>
            <Textarea rows={6} value={raw} onChange={(e) => setRaw(e.target.value)}
              placeholder={"Ramesh: Civil work started on second floor today\nSuresh: Plywood delivered, 20 sheets\nAmit: Client approved the wardrobe design"}
              data-testid="ingest-textarea" />
            <div className="flex flex-wrap gap-2">
              <Button onClick={parseAndIngest} disabled={!groupId || !raw.trim()} className="rounded-full" data-testid="ingest-button">
                <Send className="h-4 w-4 mr-1" /> Ingest Messages
              </Button>
              <Button onClick={classify} disabled={!projectId || classifying} variant="secondary" className="rounded-full" data-testid="classify-button">
                <Sparkles className="h-4 w-4 mr-1" /> {classifying ? "Classifying…" : "Classify with AI"}
              </Button>
            </div>
            {!groupId && projectId && projectGroups.length === 0 && (
              <p className="text-xs text-muted-foreground">Map a WhatsApp group to this project first (Project → WhatsApp Groups).</p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">Select a project to view its messages.</p>
        ) : messages.map((m) => (
          <Card key={m.id} className={m.classification ? "tracing-border" : ""} data-testid={`message-${m.id}`}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1">
                  <div className="text-xs text-muted-foreground">{m.sender_name} · {new Date(m.sent_at).toLocaleString("en-IN")}</div>
                  <div className="text-sm mt-0.5">{m.body}</div>
                  {m.classification && (
                    <div className="mt-2 text-xs text-primary/80 italic">↳ {m.classification.summary_line}</div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  {m.classification ? (
                    <>
                      <StatusBadge status={m.classification.category} />
                      {m.classification.stage_name && <span className="text-[10px] text-muted-foreground">{m.classification.stage_name}</span>}
                      {m.classification.is_correct != null ? (
                        <span className="text-[10px] text-green-600">{m.classification.model === "human_correction" ? "corrected" : "confirmed"}</span>
                      ) : canEdit && (
                        <div className="flex gap-1 mt-1">
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => markCorrect(m.id)} data-testid={`mark-correct-${m.id}`}><Check className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => openReview(m)} data-testid={`edit-classification-${m.id}`}><PencilIcon className="h-3.5 w-3.5" /></Button>
                        </div>
                      )}
                    </>
                  ) : m.is_relevant ? (
                    <span className="text-[10px] text-muted-foreground">unclassified</span>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">filtered</span>
                  )}
                </div>
              </div>
              {reviewingId === m.id && (
                <div className="mt-3 pt-3 border-t border-border space-y-2">
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Select value={reviewForm.category} onValueChange={(v) => setReviewForm({ ...reviewForm, category: v })}>
                      <SelectTrigger data-testid={`review-category-${m.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={reviewForm.stage_id} onValueChange={(v) => setReviewForm({ ...reviewForm, stage_id: v, substage_id: "" })}>
                      <SelectTrigger data-testid={`review-stage-${m.id}`}><SelectValue placeholder="Stage" /></SelectTrigger>
                      <SelectContent>{(projectDetail?.stages || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.stage_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {reviewForm.stage_id && (
                    <Select value={reviewForm.substage_id} onValueChange={(v) => setReviewForm({ ...reviewForm, substage_id: v })}>
                      <SelectTrigger data-testid={`review-substage-${m.id}`}><SelectValue placeholder="Substage" /></SelectTrigger>
                      <SelectContent>{substagesForStage(reviewForm.stage_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.substage_name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <Input value={reviewForm.summary_line} onChange={(e) => setReviewForm({ ...reviewForm, summary_line: e.target.value })} data-testid={`review-summary-${m.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" className="rounded-full" onClick={() => submitCorrection(m.id)} data-testid={`save-correction-${m.id}`}>Save correction</Button>
                    <Button size="sm" variant="ghost" onClick={() => setReviewingId(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
